import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Typography, Space, Spin, Empty, Input, Row, Col, Modal, Form, ColorPicker, Select, Dropdown, MenuProps, Tag } from 'antd';
import { PlusOutlined, MoreOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { tasksApi } from '../../api/client';
import { useAuthStore } from '../../store/auth.store';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

export default function BoardsList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthStore();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [filter, setFilter] = useState<'all' | 'my' | 'archived'>('all');

    const { data: boards, isLoading } = useQuery({
        queryKey: ['boards'],
        queryFn: () => tasksApi.getBoards().then(res => res.data),
    });

    const createBoardMutation = useMutation({
        mutationFn: (data: any) => tasksApi.createBoard(data).then(res => res.data),
        onSuccess: (newBoard) => {
            queryClient.invalidateQueries({ queryKey: ['boards'] });
            setIsCreateModalOpen(false);
            form.resetFields();
            navigate(`/tasks/${newBoard.id}`);
        },
    });

    const deleteBoardMutation = useMutation({
        mutationFn: (id: string) => tasksApi.deleteBoard(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['boards'] });
        },
    });

    const handleDelete = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        Modal.confirm({
            title: 'Czy na pewno chcesz usunąć tę tablicę?',
            icon: <ExclamationCircleOutlined />,
            content: 'Tej operacji nie można prosto cofnąć (wymaga dostępu do bazy). Wszystkie listy i karty zostaną usunięte.',
            okText: 'Usuń',
            okType: 'danger',
            cancelText: 'Anuluj',
            onOk() {
                deleteBoardMutation.mutate(id);
            },
        });
    };

    const handleCreateSubmit = (values: any) => {
        createBoardMutation.mutate({
            ...values,
            backgroundColor: typeof values.backgroundColor === 'string'
                ? values.backgroundColor
                : values.backgroundColor?.toHexString() || '#1890ff',
            ownerId: user?.id
        });
    };

    if (isLoading) {
        return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
    }

    const filteredBoards = (boards || []).filter((board: any) => {
        if (filter === 'my') return board.owner?.id === user?.id || board.ownerId === user?.id;
        // In this basic version everything else is "all" 
        // Real implementation of 'archived' depends on if 'isArchived' or 'status' field exists.
        return true;
    });

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0 }}>Tablice zadań</Title>
                <Space>
                    <Select value={filter} onChange={setFilter} style={{ width: 140 }}>
                        <Option value="all">Wszystkie tablice</Option>
                        <Option value="my">Moje tablice</Option>
                    </Select>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalOpen(true)}>
                        Nowa tablica
                    </Button>
                </Space>
            </div>

            {filteredBoards.length === 0 ? (
                <Empty description="Brak tablic pasujących do kryteriów" />
            ) : (
                <Row gutter={[16, 16]}>
                    {filteredBoards.map((board: any) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={board.id}>
                            <Card
                                hoverable
                                onClick={() => navigate(`/tasks/${board.id}`)}
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderColor: '#e8e8e8',
                                    overflow: 'hidden',
                                }}
                                bodyStyle={{
                                    padding: '16px',
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                                cover={
                                    <div style={{
                                        height: 80,
                                        width: '100%',
                                        backgroundColor: board.backgroundColor || '#1890ff',
                                        backgroundImage: board.backgroundImage ? `url(${board.backgroundImage})` : 'none',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                    }} />
                                }
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Title level={5} style={{ margin: 0, paddingRight: 8 }} ellipsis={{ rows: 2 }}>{board.name}</Title>
                                    <Dropdown
                                        menu={{
                                            items: [
                                                { key: 'delete', label: 'Usuń tablicę', danger: true, onClick: (e) => handleDelete(board.id, e.domEvent as any) }
                                            ]
                                        }}
                                        trigger={['click']}
                                    >
                                        <Button
                                            type="text"
                                            icon={<MoreOutlined />}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ margin: '-4px -8px 0 0' }}
                                        />
                                    </Dropdown>
                                </div>

                                <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginTop: 8, marginBottom: 'auto', minHeight: 44 }}>
                                    {board.description || 'Brak opisu'}
                                </Paragraph>

                                <div style={{ marginTop: 16 }}>
                                    <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Utworzono: {dayjs(board.createdAt).format('DD.MM.YYYY')}
                                        </Text>
                                        <Space>
                                            {board.visibility === 'public' && <Tag color="green" style={{ margin: 0 }}>Publiczna</Tag>}
                                            {board.visibility === 'team' && <Tag color="blue" style={{ margin: 0 }}>Zespół</Tag>}
                                            {board.visibility === 'private' && <Tag style={{ margin: 0 }}>Prywatna</Tag>}
                                        </Space>
                                    </Space>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <Modal
                title="Utwórz nową tablicę"
                open={isCreateModalOpen}
                onCancel={() => { setIsCreateModalOpen(false); form.resetFields(); }}
                onOk={() => form.submit()}
                confirmLoading={createBoardMutation.isPending}
            >
                <Form form={form} layout="vertical" onFinish={handleCreateSubmit} initialValues={{ visibility: 'team', backgroundColor: '#1890ff' }}>
                    <Form.Item name="name" label="Nazwa tablicy" rules={[{ required: true, message: 'Nazwa jest wymagana' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Opis">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="visibility" label="Widoczność">
                        <Select>
                            <Option value="private">Prywatna (Tylko ja)</Option>
                            <Option value="team">Zespołowa (Moja firma)</Option>
                            <Option value="public">Publiczna (Wszyscy)</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="backgroundColor" label="Kolor tła">
                        <ColorPicker />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
